import {
  Button,
  Divider,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Box,
  Badge,
  IconButton,
} from "@mui/material";

import { Link, NavLink } from "react-router-dom";

import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import DescriptionIcon from "@mui/icons-material/Description";
import TuneIcon from "@mui/icons-material/Tune";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PsychologyAltIcon from "@mui/icons-material/PsychologyAlt";
import CurrencyPoundIcon from "@mui/icons-material/CurrencyPound";
import ApiIcon from "@mui/icons-material/Api";
import HistoryIcon from "@mui/icons-material/History";
import SystemUpdateAltIcon from "@mui/icons-material/SystemUpdateAlt";
import LogoutIcon from "@mui/icons-material/Logout";

import {
  HomeRoute,
  ProductsRoute,
  PurchaseOrdersRoute,
  QuotesRoute,
  BillsRoute,
  SuppliersRoute,
  StockValueRoute,
  AdjustStockRoute,
  LocationsRoute,
  CustomSalesRoute,
  AuditLogRoute,
} from "./routes";

import { useUpdate } from "./context/update.provider";
import { useAppAuth } from "./hooks/useAppAuth";
import { useUserRole } from "./hooks/useUserRole";
import axios from "axios";
import ButtonWithConfirm from "./components/modal.confirm-dialog.component";

export const Sidebar = () => {
  const drawerWidth = 240;
  
  // Version information from build-time injection
  const gitCommitHash = __VITE_GIT_COMMIT_HASH__ || 'unknown';
  const buildTimestamp = __VITE_BUILD_TIMESTAMP__ || 'unknown';
  
  // Update context
  const { isUpdateAvailable, showUpdateModal, forceCheck, lastChecked } = useUpdate();

  // Auth context for logout
  const { logout } = useAppAuth();

  // User role for conditional rendering
  const { isAdmin } = useUserRole();

  const handleLogout = () => {
    // Clear localStorage (Auth0 SDK will handle most of this, but be explicit)
    localStorage.clear();

    // Clear axios authorization header
    delete axios.defaults.headers.common['Authorization'];

    // Call Auth0 logout
    logout({
      logoutParams: {
        returnTo: window.location.origin
      }
    });
  };

  const handleVersionClick = () => {
    if (isUpdateAvailable) {
      showUpdateModal();
    } else {
      const versionInfo = `AMS Admin v.${gitCommitHash} • Built ${buildTimestamp}`;
      navigator.clipboard.writeText(versionInfo).catch(() => {
        // Fallback for browsers that don't support clipboard API
        console.log('Version:', versionInfo);
      });
    }
  };

  const handleVersionRightClick = (e: React.MouseEvent) => {
    e.preventDefault();
    forceCheck();
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: "none", sm: "block" },
        "& .MuiDrawer-paper": { boxSizing: "border-box", width: drawerWidth },
      }}
    >
      <Toolbar>
        <NavLink to={HomeRoute}>
          <Button variant="text" style={{ backgroundColor: "transparent" }}>
            <img
              src="/logo.png"
              width={drawerWidth * 0.8}
              alt="A Major Inventory Services"
            />
          </Button>
        </NavLink>
      </Toolbar>
      <Divider />
      <List dense={true} component="nav">
        <ListItemButton component={Link} to={ProductsRoute} key="products">
          <ListItemIcon>
            <Inventory2Icon />
          </ListItemIcon>
          <ListItemText primary="Inventory" />
        </ListItemButton>
        {/* ADMIN ONLY: Stock Value */}
        {isAdmin && (
          <ListItemButton component={Link} to={StockValueRoute} key="stock-value">
            <ListItemIcon>
              <CurrencyPoundIcon />
            </ListItemIcon>
            <ListItemText primary="Stock Value" />
          </ListItemButton>
        )}
        <ListItemButton
          component={Link}
          to={AdjustStockRoute}
          key="purchase-orders-create"
        >
          <ListItemIcon>
            <TuneIcon />
          </ListItemIcon>

          <ListItemText primary="Adjust Stock" />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={PurchaseOrdersRoute}
          key="purchase-orders"
        >
          <ListItemIcon>
            <ListAltIcon />
          </ListItemIcon>
          <ListItemText primary="Purchase Orders" />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={QuotesRoute}
          key="quotes"
        >
          <ListItemIcon>
            <DescriptionIcon />
          </ListItemIcon>
          <ListItemText primary="Quotes" />
        </ListItemButton>
        {/* ADMIN ONLY: Bills Management */}
        {isAdmin && (
          <ListItemButton
            component={Link}
            to={BillsRoute}
            key="bills"
          >
            <ListItemIcon>
              <RequestQuoteIcon />
            </ListItemIcon>
            <ListItemText primary="Bills Management" />
          </ListItemButton>
        )}

        <Divider sx={{ m: 2 }} />

        <ListItemButton
          component={Link}
          to={SuppliersRoute}
          key="suppliers"
        >
          <ListItemIcon>
            <PeopleAltIcon />
          </ListItemIcon>
          <ListItemText primary="Suppliers" />
        </ListItemButton>
        <ListItemButton
          component={Link}
          to={CustomSalesRoute}
          key="custom-sales"
        >
          <ListItemIcon>
            <PsychologyAltIcon />
          </ListItemIcon>
          <ListItemText primary="Custom Sales" />
        </ListItemButton>
        <ListItemButton component={Link} to={LocationsRoute} key="locations">
          <ListItemIcon>
            <WarehouseIcon />
          </ListItemIcon>
          <ListItemText primary="Locations" />
        </ListItemButton>
        {/* ADMIN ONLY: Audit Logs */}
        {isAdmin && (
          <ListItemButton component={Link} to={AuditLogRoute} key="audit-logs">
            <ListItemIcon>
              <HistoryIcon />
            </ListItemIcon>
            <ListItemText primary="Audit Logs" />
          </ListItemButton>
        )}
        <Divider sx={{ m: 2 }} />
        <ListItemButton
          onClick={() => {
            window.open(
              "https://a-major-music.myshopify.com/admin/draft_orders/new"
            );
          }}
          key="create-order"
        >
          <ListItemIcon>
            <OpenInNewIcon />
          </ListItemIcon>
          <ListItemText primary="Create Order" />
        </ListItemButton>
        <ListItemButton
          onClick={() => {
            window.open(
              "https://docs.google.com/forms/d/e/1FAIpQLSdLA1SMr_uFE_2G4prSBUoraHiX80oS6pB-3LImli7ERCW73w/viewform"
            );
          }}
          key="create-spe"
        >
          <ListItemIcon>
            <OpenInNewIcon />
          </ListItemIcon>
          <ListItemText primary="Create SPE" />
        </ListItemButton>
        <Divider sx={{ m: 2 }} />
        {/* ADMIN ONLY: API Documentation */}
        {isAdmin && (
          <ListItemButton
            onClick={() => {
              const apiUrl =
                import.meta.env.MODE === "production"
                  ? "https://ams-admin-v2-server.amajormusic.co.uk/api-docs"
                  : "http://localhost:3000/api-docs";
              window.open(apiUrl, "_blank");
            }}
            key="api-docs"
          >
            <ListItemIcon>
              <ApiIcon />
            </ListItemIcon>
            <ListItemText primary="API Documentation" />
          </ListItemButton>
        )}
      </List>

      {/* Bottom section: Logout button and version info */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: 'white',
          pt: 2,
          pb: 2,
        }}
      >
        {/* Logout button */}
        <Box
          sx={{
            px: 2,
            mb: 2,
          }}
        >
        <ButtonWithConfirm
          title="Sign Out?"
          message="Are you sure you want to sign out of AMS Admin?"
          okButtonLabel="Sign Out"
          cancelButtonLabel="Cancel"
          onOk={handleLogout}
          variant="contained"
          color="error"
          size="small"
          sx={{ width: '100%' }}
        >
          <LogoutIcon sx={{ mr: 1 }} />
          Sign Out
        </ButtonWithConfirm>
        </Box>

        {/* Version information */}
        <Box
          sx={{
            px: 2,
            textAlign: 'center',
          }}
        >
        {isUpdateAvailable ? (
          <IconButton
            onClick={handleVersionClick}
            size="small"
            sx={{
              mb: 1,
              color: 'primary.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': {
                '0%': {
                  transform: 'scale(1)',
                  opacity: 1,
                },
                '50%': {
                  transform: 'scale(1.05)',
                  opacity: 0.8,
                },
                '100%': {
                  transform: 'scale(1)',
                  opacity: 1,
                }
              }
            }}
            title="New version available! Click to update"
          >
            <Badge 
              badgeContent="!" 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.6rem',
                  minWidth: '12px',
                  height: '12px',
                }
              }}
            >
              <SystemUpdateAltIcon />
            </Badge>
          </IconButton>
        ) : null}
        
        <Typography 
          variant="caption" 
          color="text.secondary"
          sx={{ 
            cursor: 'pointer',
            fontSize: '0.75rem',
            opacity: isUpdateAvailable ? 1 : 0.7,
            color: isUpdateAvailable ? 'primary.main' : 'text.secondary',
            display: 'block',
            '&:hover': {
              opacity: 1,
              color: 'primary.main'
            }
          }}
          onClick={handleVersionClick}
          onContextMenu={handleVersionRightClick}
          title={isUpdateAvailable ? "New version available! Click to update" : "Click to copy version info • Right-click to force check"}
        >
          v.{gitCommitHash} • {buildTimestamp}
          {isUpdateAvailable && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                fontSize: '0.6rem', 
                color: 'primary.main',
                fontWeight: 'bold',
                mt: 0.5 
              }}
            >
              (Update Available)
            </Typography>
          )}
          {lastChecked && (
            <Typography 
              variant="caption" 
              sx={{ 
                display: 'block', 
                fontSize: '0.6rem', 
                opacity: 0.5, 
                mt: 0.5 
              }}
            >
              Last checked: {lastChecked.toLocaleTimeString()}
            </Typography>
          )}
        </Typography>
        </Box>
      </Box>
    </Drawer>
  );
};
